import { Route } from 'react-router-dom'
import { Building2, CalendarRange, ClipboardCheck, LayoutDashboard, LifeBuoy, ListTree, Map as MapIcon, TrendingDown } from 'lucide-react'
import type { EditionSection } from '@agribot/polygon-checks-ui'
import { ReviewStoreContext } from './hooks'
import { createApiReviewStore } from './api-store'
import type { ReviewStore } from './store'
import { CampaignDashboard } from './screens/CampaignDashboard'
import { CampaignDetail, Campaigns } from './screens/Campaigns'
import { OriginsMap } from './screens/OriginsMap'
import { HelpRequests } from './screens/HelpRequests'
import { ReviewQueue } from './screens/ReviewQueue'
import { Scenarios } from './screens/Scenarios'
import { Suppliers } from './screens/Suppliers'
import { Taxonomy } from './screens/Taxonomy'
import { ReviewSubmission } from './screens/ReviewSubmission'

/**
 * The buyer's sourcing workspace, as a section of the application: the
 * review queues and reviewing a submission. Staff only; a supplier's or
 * farmer's login, or a key, has no business here, and the API refuses them
 * on these routes regardless.
 *
 * `store` defaults to eudr-api; a host or a test may pass its own.
 */
export function sourcingSection(store: ReviewStore = createApiReviewStore()): EditionSection {
  const staff = (c: { kind: string; anyKey: boolean }) => c.kind === 'staff' && !c.anyKey
  const wrap = (node: React.ReactNode) => <ReviewStoreContext.Provider value={store}>{node}</ReviewStoreContext.Provider>
  return {
    title: 'Sourcing',
    feature: 'sourcing',
    nav: [
      { to: '/sourcing/dashboard', label: 'Dashboard', Icon: LayoutDashboard, show: staff },
      { to: '/sourcing/reviews', label: 'Reviews', Icon: ClipboardCheck, show: staff },
      { to: '/sourcing/requests', label: 'Supplier requests', Icon: LifeBuoy, show: staff },
      { to: '/sourcing/origins', label: 'Origins map', Icon: MapIcon, show: staff },
      { to: '/sourcing/scenarios', label: 'Scenarios', Icon: TrendingDown, show: staff },
      { to: '/sourcing/campaigns', label: 'Campaigns', Icon: CalendarRange, show: staff },
      { to: '/sourcing/suppliers', label: 'Suppliers', Icon: Building2, show: staff },
      { to: '/sourcing/taxonomy', label: 'Taxonomy', Icon: ListTree, show: staff },
    ],
    routes: (can) => (staff(can)
      ? [
          <Route key="dashboard" path="/sourcing/dashboard" element={wrap(<CampaignDashboard />)} />,
          <Route key="reviews" path="/sourcing/reviews" element={wrap(<ReviewQueue />)} />,
          <Route key="review" path="/sourcing/reviews/:submissionId" element={wrap(<ReviewSubmission />)} />,
          <Route key="requests" path="/sourcing/requests" element={<HelpRequests />} />,
          <Route key="origins" path="/sourcing/origins" element={wrap(<OriginsMap />)} />,
          <Route key="scenarios" path="/sourcing/scenarios" element={wrap(<Scenarios />)} />,
          <Route key="campaigns" path="/sourcing/campaigns" element={<Campaigns />} />,
          <Route key="campaign" path="/sourcing/campaigns/:campaignId" element={<CampaignDetail />} />,
          <Route key="suppliers" path="/sourcing/suppliers" element={wrap(<Suppliers />)} />,
          <Route key="taxonomy" path="/sourcing/taxonomy" element={<Taxonomy />} />,
        ]
      : []),
  }
}
